import { useEffect, useId, useRef, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { ApiError } from '@/lib/api'
import { Icon, type IconName } from '@/components/Icon'
import '@/styles/record.css'

/**
 * Композер над лентой (CompRecord.dc.html): комментарий, звонок, задача одним полем.
 *
 * Режимы передаёт карточка — каждый со своей отправкой и только если на него есть право и
 * API. Компонент не знает, куда уходит текст: комментарий и задача у сделки живут в разных
 * таблицах и под разными правами, и решать это за карточку значило бы завести второй
 * реестр прав.
 *
 * Ошибка **оставляет текст в поле**: набранный абзац, потерянный из-за таймаута, второй раз
 * не напишут.
 */
export interface ComposerValue {
  text: string
  /** Срок задачи из `datetime-local` (местное время, без пояса). */
  due?: string
  /** Длительность звонка в минутах. */
  durationMinutes?: number
  /** Упомянутые (#114) — идентификаторы выбранных в поле, а не разбор `@имени` в тексте. */
  mentionedUserIds?: string[]
}

/** Кого можно упомянуть. */
export interface MentionCandidate {
  id: string
  name: string
}

export interface ComposerMode {
  id: string
  label: string
  icon: IconName
  placeholder: string
  /** Дополнительное поле режима. */
  extra?: 'due' | 'duration'
  /**
   * Поиск упоминаемых (#114). Есть — под полем появляется выбор коллеги. Передаётся
   * функцией: композер не знает эндпоинтов, это решает карточка.
   */
  mentions?: (query: string, signal: AbortSignal) => Promise<MentionCandidate[]>
  submit: (value: ComposerValue) => Promise<unknown>
}

export interface ComposerProps {
  modes: ComposerMode[]
  /** Управляемый режим — чтобы «Назначить шаг» мог открыть задачу. */
  mode?: string
  onModeChange?: (mode: string) => void
  /** Меняется — поле получает фокус (кнопка «Назначить шаг» снаружи). */
  focusKey?: number
}

export function Composer({ modes, mode: controlled, onModeChange, focusKey }: ComposerProps) {
  const { t } = useApp()
  const [own, setOwn] = useState(modes[0]?.id ?? '')
  const active = modes.find((candidate) => candidate.id === (controlled ?? own)) ?? modes[0]
  const [text, setText] = useState('')
  const [due, setDue] = useState('')
  const [duration, setDuration] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [picked, setPicked] = useState<MentionCandidate[]>([])
  const fieldRef = useRef<HTMLTextAreaElement>(null)
  const fieldId = useId()

  useEffect(() => {
    if (focusKey) fieldRef.current?.focus()
  }, [focusKey])

  if (!active) return null

  const choose = (id: string) => {
    setOwn(id)
    onModeChange?.(id)
    setError(null)
  }

  const submit = async () => {
    if (text.trim() === '' || pending) return
    setPending(true)
    setError(null)
    try {
      await active.submit({
        text: text.trim(),
        due: active.extra === 'due' && due !== '' ? due : undefined,
        durationMinutes: active.extra === 'duration' && duration !== '' ? Number(duration) : undefined,
        mentionedUserIds: active.mentions ? picked.map((person) => person.id) : undefined,
      })
      setText('')
      setDue('')
      setDuration('')
      setPicked([])
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t('record.composer.failed'))
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="composer">
      {modes.length > 1 && (
        <div className="composer-tabs" role="group" aria-label={t('record.composer.kind')}>
          {modes.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={candidate.id === active.id}
              onClick={() => choose(candidate.id)}
            >
              <Icon name={candidate.icon} size="sm" />
              {candidate.label}
            </button>
          ))}
        </div>
      )}
      <div className="composer-body">
        <label htmlFor={fieldId} style={visuallyHidden}>
          {active.label}
        </label>
        <textarea
          id={fieldId}
          ref={fieldRef}
          rows={2}
          value={text}
          placeholder={active.placeholder}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
              event.preventDefault()
              void submit()
            }
          }}
        />
      </div>
      {active.mentions && (
        <MentionPicker
          search={active.mentions}
          picked={picked}
          onPick={(person) => {
            if (picked.some((candidate) => candidate.id === person.id)) return
            setPicked([...picked, person])
            // The name goes into the text for the reader; the id goes to the server.
            setText((current) => `${current}${current === '' || current.endsWith(' ') ? '' : ' '}@${person.name} `)
            fieldRef.current?.focus()
          }}
          onRemove={(id) => setPicked(picked.filter((person) => person.id !== id))}
        />
      )}
      {active.extra === 'due' && (
        <div className="composer-extra">
          <label>
            {t('record.composer.due')}{' '}
            <input className="input" type="datetime-local" value={due} onChange={(event) => setDue(event.target.value)} />
          </label>
        </div>
      )}
      {active.extra === 'duration' && (
        <div className="composer-extra">
          <label>
            {t('record.composer.duration')}{' '}
            <input
              className="input"
              type="number"
              min="0"
              step="1"
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
            />
          </label>
        </div>
      )}
      {error && (
        <div className="composer-extra" role="alert" style={{ color: 'var(--danger-text)' }}>
          {error}
        </div>
      )}
      <div className="composer-foot">
        <span className="t-xs muted">{t('record.composer.hint')}</span>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={pending || text.trim() === ''}
          onClick={() => void submit()}
        >
          {t('record.composer.add')}
        </button>
      </div>
    </div>
  )
}

/**
 * Выбор упоминаемого (#114): поиск по имени, найденные — кнопками, выбранные — плашками.
 *
 * Упоминание уходит на сервер идентификатором выбранного, а не разбором `@имени` из текста:
 * двух однофамильцев по имени не различить, а опечатка молча не уведомила бы никого — и
 * автор ждал бы ответа, который не придёт.
 */
function MentionPicker({
  search,
  picked,
  onPick,
  onRemove,
}: {
  search: NonNullable<ComposerMode['mentions']>
  picked: MentionCandidate[]
  onPick: (person: MentionCandidate) => void
  onRemove: (id: string) => void
}) {
  const { t } = useApp()
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<MentionCandidate[]>([])
  const [failed, setFailed] = useState(false)
  // The search arrives as a fresh closure on every render of the card; keyed on it, the
  // effect would refetch after its own result renders — a loop. The query is the key.
  const searchRef = useRef(search)
  searchRef.current = search

  useEffect(() => {
    const needle = query.trim()
    if (needle === '') {
      setFound([])
      setFailed(false)
      return
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      searchRef.current(needle, controller.signal).then(
        (people) => {
          setFound(people)
          setFailed(false)
        },
        () => {
          if (!controller.signal.aborted) setFailed(true)
        },
      )
    }, 150)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query])

  const offered = found.filter((person) => !picked.some((chosen) => chosen.id === person.id))

  return (
    <div className="composer-extra stack" style={{ gap: 6 }}>
      <label>
        {t('record.composer.mention')}{' '}
        <input
          className="input"
          type="search"
          value={query}
          placeholder={t('record.composer.mentionPlaceholder')}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      {failed && (
        <span role="alert" className="t-sm" style={{ color: 'var(--danger-text)' }}>
          {t('record.composer.mentionFailed')}
        </span>
      )}
      {offered.length > 0 && (
        <div className="row" role="group" aria-label={t('record.composer.mentionFound')} style={{ gap: 4, flexWrap: 'wrap' }}>
          {offered.map((person) => (
            <button
              key={person.id}
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                onPick(person)
                setQuery('')
              }}
            >
              {person.name}
            </button>
          ))}
        </div>
      )}
      {picked.length > 0 && (
        <div className="row" aria-label={t('record.composer.mentioned')} style={{ gap: 4, flexWrap: 'wrap' }}>
          {picked.map((person) => (
            <span key={person.id} className="badge">
              @{person.name}{' '}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                aria-label={t('record.composer.unmention', { name: person.name })}
                onClick={() => onRemove(person.id)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/** Подпись для диктора: поле без видимой метки, у него есть заголовок режима. */
const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
}
